import React, { useRef, useState } from "react";

// 수학 그리기·색칠 문항: 문제 그림 위에 손가락 · 펜으로 그린다(2026-10-26 대표). 선생님이 보고 매긴다.
// 서버: api/assignment/math_drawing.py. 답은 { stroke_list: [{ color, width, points: [[x, y], ...] }] }
// - 좌표는 그림 비율(0~1)이다. 화면 크기가 달라도 선생님 화면에 같은 자리로 그려진다.
// - 그림이 없는 문항은 모눈 빈칸에 그린다.
// - 지우개는 손이 지나간 획을 통째로 지운다.

export const DRAWING_VIEW_WIDTH = 1000;
const BLANK_VIEW_HEIGHT = 600;
const PEN_COLOR_LIST = ["#1F2328", "#E0312B", "#2F6FDB", "#1F9D6B", "#F2C200"];
const PEN_WIDTH = 5;
const HIGHLIGHT_WIDTH = 26;
const MINIMUM_POINT_DISTANCE = 0.003;
const MAXIMUM_POINT_COUNT = 600;
const MAXIMUM_STROKE_COUNT = 300;
const ERASER_RADIUS = 22;

const roundRatio = (value) => Math.round(Math.min(Math.max(value, 0), 1) * 10000) / 10000;

export const strokeListOf = (value) => (value && Array.isArray(value.stroke_list) ? value.stroke_list : []);

// 그린 획. viewHeight 는 그림 비율에 맞춘 viewBox 높이
export const DrawingStrokes = ({ strokeList, viewHeight }) =>
  strokeList.map((stroke, index) => (
    <polyline
      key={index}
      points={(stroke.points || [])
        .map(([x, y]) => `${(x * DRAWING_VIEW_WIDTH).toFixed(1)},${(y * viewHeight).toFixed(1)}`)
        .join(" ")}
      fill="none"
      stroke={stroke.color}
      strokeWidth={stroke.width || PEN_WIDTH}
      strokeOpacity={stroke.opacity ?? 1}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ));

// 그림과 그 위의 획. 그리기 판과 선생님이 보는 화면이 같이 쓴다
export const DrawingBoard = ({ imageUrl, strokeList, svgProps = {}, liveStroke = null, className = "" }) => {
  const [viewHeight, setViewHeight] = useState(BLANK_VIEW_HEIGHT);
  return (
    <div className={`math-drawing__board${imageUrl ? "" : " math-drawing__board--blank"} ${className}`}>
      {imageUrl ? (
        <img
          className="math-drawing__image"
          src={imageUrl}
          alt=""
          draggable={false}
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget;
            if (naturalWidth && naturalHeight) setViewHeight(Math.round((DRAWING_VIEW_WIDTH * naturalHeight) / naturalWidth));
          }}
        />
      ) : (
        <div className="math-drawing__blank" style={{ aspectRatio: `${DRAWING_VIEW_WIDTH} / ${BLANK_VIEW_HEIGHT}` }} />
      )}
      <svg
        className="math-drawing__layer"
        viewBox={`0 0 ${DRAWING_VIEW_WIDTH} ${viewHeight}`}
        preserveAspectRatio="none"
        data-view-height={viewHeight}
        {...svgProps}
      >
        <DrawingStrokes strokeList={liveStroke ? [...strokeList, liveStroke] : strokeList} viewHeight={viewHeight} />
      </svg>
    </div>
  );
};

const MathDrawingPad = ({ imageUrl, value, onChange, disabled }) => {
  const strokeList = strokeListOf(value);
  const drawingRef = useRef(null);
  const [liveStroke, setLiveStroke] = useState(null);
  const [color, setColor] = useState(PEN_COLOR_LIST[0]);
  const [tool, setTool] = useState("pen");

  const save = (nextStrokeList) => onChange(nextStrokeList.length ? { stroke_list: nextStrokeList } : null);

  const ratioPointOf = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return [roundRatio((event.clientX - rect.left) / rect.width), roundRatio((event.clientY - rect.top) / rect.height)];
  };

  const eraseAt = (event, [x, y]) => {
    const viewHeight = Number(event.currentTarget.dataset.viewHeight) || BLANK_VIEW_HEIGHT;
    const remaining = strokeList.filter(
      (stroke) =>
        !(stroke.points || []).some(
          ([pointX, pointY]) => Math.hypot((pointX - x) * DRAWING_VIEW_WIDTH, (pointY - y) * viewHeight) < ERASER_RADIUS
        )
    );
    if (remaining.length !== strokeList.length) save(remaining);
  };

  const handlePointerDown = (event) => {
    if (disabled) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = ratioPointOf(event);
    if (tool === "eraser") {
      drawingRef.current = { isErasing: true };
      eraseAt(event, point);
      return;
    }
    if (strokeList.length >= MAXIMUM_STROKE_COUNT) return;
    const isHighlight = tool === "highlight";
    drawingRef.current = {
      color,
      width: isHighlight ? HIGHLIGHT_WIDTH : PEN_WIDTH,
      ...(isHighlight ? { opacity: 0.45 } : {}),
      points: [point],
    };
    setLiveStroke(drawingRef.current);
  };

  const handlePointerMove = (event) => {
    const current = drawingRef.current;
    if (disabled || !current) return;
    const point = ratioPointOf(event);
    if (current.isErasing) {
      eraseAt(event, point);
      return;
    }
    const last = current.points[current.points.length - 1];
    if (Math.hypot(point[0] - last[0], point[1] - last[1]) < MINIMUM_POINT_DISTANCE) return;
    if (current.points.length >= MAXIMUM_POINT_COUNT) return;
    drawingRef.current = { ...current, points: [...current.points, point] };
    setLiveStroke(drawingRef.current);
  };

  const handlePointerUp = () => {
    const current = drawingRef.current;
    drawingRef.current = null;
    setLiveStroke(null);
    if (!current || current.isErasing) return;
    // 한 번 톡 찍은 것도 점으로 남긴다
    const points = current.points.length === 1 ? [current.points[0], current.points[0]] : current.points;
    save([...strokeList, { ...current, points }]);
  };

  return (
    <div className="math-drawing">
      {!disabled && (
        <div className="math-drawing__tools">
          {PEN_COLOR_LIST.map((one) => (
            <button
              key={one}
              type="button"
              aria-label="펜 색"
              className={`math-drawing__color${tool !== "eraser" && color === one ? " is-picked" : ""}`}
              style={{ background: one }}
              onClick={() => {
                setColor(one);
                if (tool === "eraser") setTool("pen");
              }}
            />
          ))}
          <span className="math-drawing__divider" />
          <button type="button" className={`math-drawing__tool${tool === "pen" ? " is-picked" : ""}`} onClick={() => setTool("pen")}>
            펜
          </button>
          <button
            type="button"
            className={`math-drawing__tool${tool === "highlight" ? " is-picked" : ""}`}
            onClick={() => setTool("highlight")}
          >
            색칠
          </button>
          <button
            type="button"
            className={`math-drawing__tool${tool === "eraser" ? " is-picked" : ""}`}
            onClick={() => setTool("eraser")}
          >
            지우개
          </button>
          <button
            type="button"
            className="math-drawing__tool"
            disabled={!strokeList.length}
            onClick={() => save(strokeList.slice(0, -1))}
          >
            되돌리기
          </button>
        </div>
      )}
      <DrawingBoard
        imageUrl={imageUrl}
        strokeList={strokeList}
        liveStroke={liveStroke}
        className={disabled ? "" : "math-drawing__board--active"}
        svgProps={{
          onPointerDown: handlePointerDown,
          onPointerMove: handlePointerMove,
          onPointerUp: handlePointerUp,
          onPointerCancel: handlePointerUp,
        }}
      />
    </div>
  );
};

export default MathDrawingPad;
